import { useEffect, useRef } from "react";

import { ProfileDto } from "@/core/api/generated/v1";
import * as profileSlice from "@/store/profile/slice";

import { useAuthenticationInfo } from "../auth/useAuthenticationInfo";
import { useAppSelector, useAppThunkDispatch } from "../redux";

interface Props {
  /** Refetch only on init if already fetched. */
  isRefetchOnInit?: boolean;
  isSkipFetch?: boolean;
}

export interface UseUserProfileV2Result {
  isIniting: boolean;
  isInited: boolean;
  profile: ProfileDto | null;
}

/** Returns currently authenticated user profile (loads it if not loaded). */
export function useUserProfileV2(props?: Props): UseUserProfileV2Result {
  const dispatch = useAppThunkDispatch();

  const authenticationInfo = useAuthenticationInfo();

  const isProfileFetched = useAppSelector((x) => x.profile.isProfileFetched);
  const profile = useAppSelector((x) => x.profile.profile);

  const resultRef = useRef<UseUserProfileV2Result>({
    isIniting: true,
    isInited: false,
    profile: null,
  });

  if (isProfileFetched && (!resultRef.current.isInited || resultRef.current.profile !== profile)) {
    resultRef.current.isIniting = false;
    resultRef.current.isInited = true;
    resultRef.current.profile = profile || null;
  }

  useEffect(() => {
    // NB: only fetch if authenticated to avoid unnecessary requests and redirects to login on 401
    if (
      authenticationInfo.isAuthenticated &&
      !props?.isSkipFetch &&
      (!profile || props?.isRefetchOnInit)
    ) {
      dispatch(profileSlice.getProfileThrottle());
    }
  }, [authenticationInfo.isAuthenticated, props?.isRefetchOnInit, props?.isSkipFetch]);

  return resultRef.current;
}
